<template>
  <div>
    <!-- Analysis Drawer -->
    <div v-if="analysis" 
         class="fixed inset-y-0 right-0 w-[800px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-30"
         :class="analysis ? 'translate-x-0' : 'translate-x-full'"
    >
      <div class="h-full flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h3 class="text-lg font-medium text-gray-900">Content Analysis</h3>
          <button 
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-500 p-2"
          >
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <div class="p-6 space-y-6">
            <!-- Article Content -->
            <div class="bg-white p-4 rounded-lg border">
              <h4 class="font-medium text-gray-900 mb-2">{{ analysis.title }}</h4>
              
              <!-- Original Content -->
              <div class="prose prose-sm max-w-none">
                <div 
                  class="text-sm text-gray-600"
                  v-html="sanitizeContent(analysis.content)"
                ></div>
              </div>
              
              <!-- Full Content and Summary (if available) -->
              <div v-if="analysis.full_content || analysis.summarized_content" class="mt-4 border-t pt-4">
                <div v-if="analysis.summarized_content" class="mb-4">
                  <h5 class="font-medium text-gray-900 mb-2">Summary</h5>
                  <div class="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    {{ analysis.summarized_content }}
                  </div>
                </div>
                
                <div v-if="analysis.full_content" class="mt-4">
                  <h5 class="font-medium text-gray-900 mb-2">Full Article Content</h5>
                  <div 
                    class="text-sm text-gray-600 max-h-96 overflow-y-auto"
                    v-html="sanitizeContent(analysis.full_content)"
                  ></div>
                </div>
              </div>
              
              <div class="mt-4 flex items-center gap-2">
                <a 
                  :href="analysis.url" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Read original article →
                </a>
              </div>
            </div>

            <!-- Basic Info -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <h4 class="font-medium text-gray-900">Published Date</h4>
                  <p class="mt-1 text-sm text-gray-600">{{ formatDate(analysis.published_at) }}</p>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Source</h4>
                  <p class="mt-1 text-sm text-gray-600">{{ analysis.source_name }}</p>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Category</h4>
                  <p class="mt-1 text-sm text-gray-600">{{ analysis.category }}</p>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Language</h4>
                  <div class="mt-1 flex items-center gap-2">
                    <p class="text-sm text-gray-600">{{ analysis.language }}</p>
                    <button 
                      @click="showLanguageSelector = true" 
                      class="text-xs text-indigo-600 hover:text-indigo-800"
                    >
                      Change
                    </button>
                    <div v-if="showLanguageSelector" class="relative">
                      <select 
                        v-model="selectedLanguage" 
                        class="block w-full rounded-md border-gray-300 py-1 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                      >
                        <option value="sr">Serbian (sr)</option>
                        <option value="en">English (en)</option>
                        <option value="hr">Croatian (hr)</option>
                        <option value="bs">Bosnian (bs)</option>
                      </select>
                      <div class="mt-2 flex gap-2">
                        <button 
                          @click="updateLanguage" 
                          class="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700"
                          :disabled="isUpdatingLanguage"
                        >
                          {{ isUpdatingLanguage ? 'Updating...' : 'Save' }}
                        </button>
                        <button 
                          @click="showLanguageSelector = false" 
                          class="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Analysis Results -->
            <div>
              <!-- Themes -->
              <div class="bg-white p-4 rounded-lg border">
                <h4 class="font-medium text-gray-900">Themes</h4>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span v-for="theme in analysis.analysis?.teme" :key="theme"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ theme }}
                  </span>
                </div>
              </div>

              <!-- Sentiment Analysis -->
              <div class="bg-white p-4 rounded-lg border">
                <h4 class="font-medium text-gray-900">Sentiment Analysis</h4>
                <div class="mt-2">
                  <div class="flex items-center gap-2">
                    <span :class="{
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium': true,
                      'bg-green-100 text-green-800': analysis.analysis?.sentiment === 'pozitivan',
                      'bg-red-100 text-red-800': analysis.analysis?.sentiment === 'negativan',
                      'bg-gray-100 text-gray-800': analysis.analysis?.sentiment === 'neutralan'
                    }">
                      {{ analysis.analysis?.sentiment }}
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-gray-600">
                    <strong>Explanation:</strong> {{ analysis.analysis?.sentiment_objasnjenje }}
                  </p>
                </div>
              </div>

              <!-- Key Actors -->
              <div class="bg-white p-4 rounded-lg border">
                <h4 class="font-medium text-gray-900">Key Actors</h4>
                <div class="mt-2 space-y-4">
                  <!-- Individuals -->
                  <div>
                    <h5 class="text-sm font-medium text-gray-700">Individuals</h5>
                    <div class="mt-1 flex flex-wrap gap-2">
                      <span v-for="actor in analysis.analysis?.akteri?.pojedinci" :key="actor"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {{ actor }}
                      </span>
                    </div>
                  </div>

                  <!-- Organizations -->
                  <div>
                    <h5 class="text-sm font-medium text-gray-700">Organizations</h5>
                    <div class="mt-1 flex flex-wrap gap-2">
                      <span v-for="org in analysis.analysis?.akteri?.organizacije" :key="org"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {{ org }}
                      </span>
                    </div>
                  </div>

                  <!-- Institutions -->
                  <div>
                    <h5 class="text-sm font-medium text-gray-700">Institutions</h5>
                    <div class="mt-1 flex flex-wrap gap-2">
                      <span v-for="inst in analysis.analysis?.akteri?.institucije" :key="inst"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {{ inst }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activism Relevance -->
              <div v-if="analysis.analysis?.aktivizam_relevantnost" class="bg-white p-4 rounded-lg border">
                <h4 class="font-medium text-gray-900">Activism Relevance</h4>
                <div class="mt-2">
                  <div class="flex items-center gap-2">
                    <span :class="{
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium': true,
                      'bg-red-100 text-red-800': analysis.analysis?.aktivizam_relevantnost === 'visoka',
                      'bg-yellow-100 text-yellow-800': analysis.analysis?.aktivizam_relevantnost === 'srednja',
                      'bg-gray-100 text-gray-800': analysis.analysis?.aktivizam_relevantnost === 'niska'
                    }">
                      {{ analysis.analysis?.aktivizam_relevantnost }}
                    </span>
                  </div>
                  <p v-if="analysis.analysis?.aktivizam_objasnjenje" class="mt-2 text-sm text-gray-600">
                    <strong>Explanation:</strong> {{ analysis.analysis?.aktivizam_objasnjenje }}
                  </p>
                </div>
              </div>

              <!-- Proposed Actions -->
              <div v-if="analysis.analysis?.predlozene_akcije?.length" class="bg-white p-4 rounded-lg border">
                <h4 class="font-medium text-gray-900">Proposed Actions</h4>
                <ul class="mt-2 list-disc list-inside space-y-2">
                  <li v-for="action in analysis.analysis?.predlozene_akcije" :key="action" 
                      class="text-sm text-gray-600">
                    {{ action }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Additional Metadata -->
            <div class="bg-white p-4 rounded-lg border">
              <h4 class="font-medium text-gray-900">Additional Information</h4>
              <div class="mt-2 grid grid-cols-2 gap-4">
                <div>
                  <h5 class="text-sm font-medium text-gray-700">Analysis Date</h5>
                  <p class="text-sm text-gray-600">{{ formatDate(analysis.analyzed_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div class="flex justify-end gap-4">
            <button
              @click="$emit('close')"
              class="button-secondary"
            >
              Close
            </button>
            <button
              @click="$emit('generate-email', analysis.id, 1)"
              class="button-primary"
            >
              Generate Email
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add a backdrop when drawer is open -->
    <div v-if="analysis" 
         class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-20"
         @click="$emit('close')">
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/date'
import DOMPurify from 'dompurify'
import type { Content } from '@/types/analysis'
import { watch, ref } from 'vue'
import { contentApi } from '@/api/content'

const props = defineProps<{
  analysis: Content | null
}>()

// Language update functionality
const showLanguageSelector = ref(false)
const selectedLanguage = ref('')
const isUpdatingLanguage = ref(false)

// Set the selected language when analysis changes
watch(() => props.analysis, (newVal) => {
  if (newVal) {
    selectedLanguage.value = newVal.language || 'sr'
  }
}, { immediate: true })

async function updateLanguage() {
  if (!props.analysis || !selectedLanguage.value) return
  
  try {
    isUpdatingLanguage.value = true
    console.log(`Updating language for content ${props.analysis.id} to ${selectedLanguage.value}`)
    
    // Use the language-specific update method
    const updatedContent = await contentApi.updateLanguage(props.analysis.id, selectedLanguage.value)
    
    console.log('Received updated content:', updatedContent)
    
    // Update the local analysis object with the new language
    if (props.analysis) {
      props.analysis.language = updatedContent.language || selectedLanguage.value
      console.log('Updated local analysis language to:', props.analysis.language)
    }
    
    showLanguageSelector.value = false
    alert(`Language updated successfully to ${selectedLanguage.value}!`)
  } catch (error) {
    console.error('Error updating language:', error)
    alert('Failed to update language. Please try again.')
  } finally {
    isUpdatingLanguage.value = false
  }
}

// Add debugging
console.log('Analysis data:', props.analysis)
console.log('Analysis details:', props.analysis?.analysis)
console.log('Full analysis object:', JSON.stringify(props.analysis, null, 2))

// Watch for changes
watch(() => props.analysis, (newVal) => {
  console.log('Analysis updated:', newVal)
  console.log('Analysis details updated:', newVal?.analysis)
  console.log('Raw analysis data:', JSON.stringify(newVal, null, 2))
}, { deep: true })

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'generate-email', id: string, count: number): void
}>()

function sanitizeContent(content: string | undefined): string {
  if (!content) return ''
  
  // Remove any <img> tags and their content
  content = content.replace(/<img[^>]*>/g, '')
  
  // Convert HTML entities
  content = content.replace(/&#160;/g, ' ')
  content = content.replace(/&nbsp;/g, ' ')
  
  // Clean the content with DOMPurify
  const cleanContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  })
  
  return cleanContent
}
</script>

<style scoped>
.button-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #4F46E5;
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.button-primary:hover {
  background-color: #4338CA;
}

.button-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background-color: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.button-secondary:hover {
  background-color: #F9FAFB;
}
</style> 